/* eslint-disable react-hooks/rules-of-hooks */
import { gql, useApolloClient, useQuery } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Loader from '@skippay/core/components/Loader';
import PageLoaderBackground from '@skippay/core/components/PageLoaderBackground';
import Redirect from '@skippay/core/components/Redirect';
import useLocation from '@skippay/core/hooks/useLocation';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';

import InSSRLoader from 'components/InSSRLoader';
import { MODALS } from 'constants/modals';
import { QUERY_DEFERRED_PAYMENT_MINI_REVOLVING_PREFERENCE } from 'constants/queryAndStorageKeys';
import { ROOT } from 'constants/routes';
import useAuthLinksByCountry from 'hooks/useAuthLinksByCountry';
import commonLabels from 'i18n/labels';
import JarabotWidgetLoader from 'jarabot/JarabotWidgetLoader';
import WarningModal from 'modals/LogoutWarningModal';
import { isLoggedInSelector, isLoggingOutSelector, sessionExpiredAction } from 'modules/auth';
import { getValidAccessToken } from 'modules/auth/utils/accessTokenUtils';
import { useModal } from 'providers/Modal/ModalContext';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';
import { afterInactivity } from 'utils/inactivityUtils';

import { useAppRedirect } from './hooks/useAppRedirect';

const INACTIVITY_TIMEOUT_NUM = process.env.INACTIVITY_TIMEOUT
	? parseInt(process.env.INACTIVITY_TIMEOUT, 10)
	: undefined;
const LAST_ACTIVITY_TIMEOUT = 60000;

const WHITE_LISTED_QUERY_PARAMS = [
	'image',
	'file',
	'text',
	'url',
	QUERY_DEFERRED_PAYMENT_MINI_REVOLVING_PREFERENCE,
];

type QueryParameters = Record<string, string | undefined>;

type RedirectQueryParameters = {
	otherParameters: QueryParameters;
	directPassQueryParameters: QueryParameters;
};

const updateTokenQuery = gql`
	query MyProfile {
		myProfile {
			id
		}
	}
`;

function RedirectUser(props: { loginLink: string }): JSX.Element {
	const { location, history } = useLocation();
	const reUtmParameter = /^utm_/i;
	const { otherParameters, directPassQueryParameters } = Object.entries(
		location.query ?? {}
	).reduce<RedirectQueryParameters>(
		(acc, [key, val]) => {
			const parameterGroup =
				reUtmParameter.test(key) || WHITE_LISTED_QUERY_PARAMS.includes(key)
					? 'directPassQueryParameters'
					: 'otherParameters';

			acc[parameterGroup][key] = val;

			return acc;
		},
		{ otherParameters: {}, directPassQueryParameters: {} }
	);

	return (
		<Redirect
			to={{
				pathname: process.env.MOB_APP_BUILD ? ROOT : props.loginLink,
				query: { ...otherParameters, ...directPassQueryParameters },
				state: {
					next:
						location.state?.next ??
						history?.createHref({
							...location,
							query: directPassQueryParameters,
						}),
				},
			}}
		/>
	);
}

function Authorized({ children }: { children: JSX.Element }): JSX.Element {
	const dispatch = useDispatch();
	const apolloClient = useApolloClient();
	const [lastActivity, setLastActivity] = useState(Date.now());
	const sessionExpiredTriggeredRef = useRef(false);
	const auth = useSelector((state) => state.auth);
	const { openModal, closeModal } = useModal();
	const intl = useIntl();

	const updateAccessToken = () =>
		apolloClient.query({
			query: updateTokenQuery,
			fetchPolicy: 'network-only',
		});

	const expireSession = () => {
		if (sessionExpiredTriggeredRef.current) {
			return false;
		}

		sessionExpiredTriggeredRef.current = true;
		closeModal(MODALS.warningLogout.id);
		dispatch(sessionExpiredAction());

		return false;
	};

	const ensureSessionIsValid = () => {
		if (getValidAccessToken()) {
			sessionExpiredTriggeredRef.current = false;

			return true;
		}

		return expireSession();
	};

	useEffect(() => {
		if (process.env.MOB_APP_BUILD && INACTIVITY_TIMEOUT_NUM) {
			return afterInactivity(INACTIVITY_TIMEOUT_NUM, () => {
				dispatch(sessionExpiredAction());
			});
		}
		return afterInactivity(1000, () => {
			setLastActivity(Date.now());
		});
	}, [dispatch]);

	useEffect(() => {
		if (process.env.MOB_APP_BUILD) {
			return undefined;
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				ensureSessionIsValid();
			}
		};

		const sessionPollInterval = setInterval(ensureSessionIsValid, 10000);

		window.addEventListener('focus', ensureSessionIsValid);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		ensureSessionIsValid();

		return () => {
			clearInterval(sessionPollInterval);
			window.removeEventListener('focus', ensureSessionIsValid);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	useEffect(() => {
		if (!process.env.MOB_APP_BUILD && auth.loginExpiresSoon) {
			if (!ensureSessionIsValid()) {
				return;
			}

			if (Date.now() > lastActivity + LAST_ACTIVITY_TIMEOUT) {
				openModal({
					...MODALS.warningLogout,
					Component: WarningModal,
					props: {
						contentLabel: intl.formatMessage({
							defaultMessage: 'Brzy budete odhlášeni',
							id: 'LogoutWarningModal.contentLabel',
						}),
						size: 'md',
						onContinue: updateAccessToken,
					},
				});
			} else {
				updateAccessToken();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [auth?.loginExpiresSoon]);

	return (
		<>
			<JarabotWidgetLoader />
			{process.env.MOB_APP_BUILD ? children : <InSSRLoader>{children}</InSSRLoader>}
		</>
	);
}

function Authentication({ children }: { children: JSX.Element }): JSX.Element {
	const isLoggedIn = useSelector(isLoggedInSelector);
	const { loginLink } = useAuthLinksByCountry();

	useAppRedirect(!isLoggedIn);

	const isLoggingOut = useSelector(isLoggingOutSelector);
	const { data: { myProfile } = {} } = useQuery(MyProfileQuery);

	if (!isLoggedIn) {
		return <RedirectUser loginLink={loginLink} />;
	}

	if (isLoggingOut || !myProfile) {
		return process.env.MOB_APP_BUILD ? (
			<PageLoaderBackground />
		) : (
			<Loader fullSize message={<FormattedMessage {...commonLabels['labels.loading']} />} />
		);
	}

	return <Authorized>{children}</Authorized>;
}

export default Authentication;
